/* eslint-disable */
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import muiSlider, { SliderRoot, SliderThumb, SliderTrack } from '@mui/material/Slider';
import { styled } from '@mui/material';
import { Card } from '../card';
import { Icon } from '../icon';

const SliderLockContext = createContext(null);

function isIos() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on isIos 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

const StyledSlider = styled(muiSlider)({
  position: 'static',
  boxSizing: 'border-box',
  height: '100%',

  '& .MuiSlider-thumb': {
    width: 'var(--start-slider-thumb-width, 68px)',
    height: 'var(--start-slider-thumb-height, 50px)',
    borderRadius: 'var(--start-slider-thumb-radius, 14px)',
    border: 'var(--start-slider-thumb-border, 3px solid transparent)',
    background:
      'var(--start-slider-thumb-bg, linear-gradient(-16deg, #6e00d4 0%, #C100B9 100%) padding-box, linear-gradient(-16deg, #C100B9 0%, #6e00d4 100%) border-box)',
    boxShadow: 'var(--start-slider-thumb-shadow, 2px 2px 2px 0 #1d1d1d)',

    '::before': {
      boxShadow: 'none',
    },
  },

  '& .MuiSlider-track': {
    height: 'var(--start-slider-track-height, 50px)',
    background: 'var(--start-slider-track-bg, linear-gradient(90deg, rgb(193, 0, 185) 0%, rgb(110, 0, 212) 100%))',
    border: 'none',
    borderRadius: 'var(--start-slider-track-radius, 14px 0 0 14px)',
    minWidth: 'var(--start-slider-track-min-width, 50px)',
  },

  '& .MuiSlider-rail': {
    backgroundColor: 'transparent',
  },
});

function ThumbComponent({ children, ...props }) {
  const thumbRef = useRef();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (thumbRef.current) {
      const { offsetWidth, parentElement } = thumbRef.current;
      if (parentElement) {
        setOffset((100 / parentElement.offsetWidth) * (offsetWidth / 2));
      }
    }
  }, []);

  const { value, min, max } = props.ownerState;
  const span = max - min;
  const left = span === 0 ? 0 : ((value - min) * 100) / span;
  const fixedLeft = Math.max(offset, Math.min(left, 100 - offset));

  return (
    <div
      style={{
        position: 'absolute',
        inset: '50% 0px 50%',
        margin: 'auto',
      }}
    >
      <SliderThumb {...props} style={{ left: `${fixedLeft}%` }} ref={thumbRef}>
        {children}
        <Icon i="arrow" size="32px" />
      </SliderThumb>
    </div>
  );
}

function TrackComponent({ children, ...props }) {
  const selectableMin = useContext(SliderLockContext);
  const { min, max, value } = props.ownerState;
  const span = max - min;
  const valuePct = span === 0 ? 0 : ((value - min) / span) * 100;
  const lockedPct = lockedZonePercent(min, max, selectableMin);
  const trackLeft = lockedPct;
  const trackWidth = Math.max(0, valuePct - lockedPct);
  const hasLockedStart = lockedPct > 0;

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
      }}
    >
      <div>
        <SliderTrack
          {...props}
          style={{
            ...props.style,
            left: `${trackLeft}%`,
            width: `${trackWidth}%`,
            minWidth: trackWidth > 0 ? undefined : 0,
            borderRadius: hasLockedStart ? '0' : undefined,
          }}
        >
          {children}
        </SliderTrack>
      </div>
    </div>
  );
}

function getThumbRightPx(containerWidth, value, min, max, thumbWidth = 68) {
  if (!containerWidth) {
    return thumbWidth;
  }
  const span = max - min;
  const ratio = span === 0 ? 0 : (value - min) / span;
  const offsetPct = (100 / containerWidth) * (thumbWidth / 2);
  const leftPct = ratio * 100;
  const fixedLeftPct = Math.max(offsetPct, Math.min(leftPct, 100 - offsetPct));
  return (fixedLeftPct / 100) * containerWidth + thumbWidth / 2;
}

function clampSliderValue(value, min, max, selectableMin) {
  const floor = selectableMin == null ? min : Math.max(min, Math.min(max, selectableMin));
  return Math.min(max, Math.max(floor, Number(value)));
}

function lockedZonePercent(min, max, selectableMin) {
  if (selectableMin == null || max <= min) return 0;
  const lockedUntil = Math.max(min, Math.min(max, selectableMin));
  if (lockedUntil <= min) return 0;
  return ((lockedUntil - min) / (max - min)) * 100;
}

export const Slider = ({ text, style, className, onChange, selectableMin, ...props }) => {
  const min = props.min ?? 0;
  const max = props.max ?? 100;
  const [value, setValue] = useState(() => clampSliderValue(props.value, min, max, selectableMin));
  const cardRef = useRef(null);
  const [hintLeftPx, setHintLeftPx] = useState(68);
  const lockedPct = lockedZonePercent(min, max, selectableMin);

  useEffect(() => {
    setValue(clampSliderValue(props.value, min, max, selectableMin));
  }, [props.value, min, max, selectableMin]);

  useEffect(() => {
    const updateHintPosition = () => {
      const width = cardRef.current?.offsetWidth ?? 0;
      setHintLeftPx(getThumbRightPx(width, value, min, max));
    };

    updateHintPosition();
    const card = cardRef.current;
    if (!card || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHintPosition);
      return () => window.removeEventListener('resize', updateHintPosition);
    }

    const observer = new ResizeObserver(updateHintPosition);
    observer.observe(card);
    return () => observer.disconnect();
  }, [value, min, max]);

  const emitChange = (event, sliderValue) => {
    const next = clampSliderValue(sliderValue, min, max, selectableMin);
    setValue(next);
    if (onChange) {
      onChange(event, next);
    }
  };

  return (
    <SliderLockContext.Provider value={selectableMin ?? null}>
      <div
        ref={cardRef}
        className={cn('start-charge-slider', className, lockedPct > 0 && 'start-charge-slider--has-locked')}
        style={style}
      >
        <Card variant="invert" className="start-charge-slider__card">
          {lockedPct > 0 ? (
            <div className="start-charge-slider__locked" style={{ width: `${lockedPct}%` }} aria-hidden="true" />
          ) : null}
          {text ? (
            <span
              className="start-charge-slider__hint"
              style={{
                left: `${hintLeftPx}px`,
                opacity: 1 - (value / 100) * 3,
              }}
            >
              {text}
            </span>
          ) : null}
          <StyledSlider
            slots={{ root: SliderRoot, thumb: ThumbComponent, track: TrackComponent }}
            {...props}
            min={min}
            max={max}
            value={value}
            onChange={(event, sliderValue) => {
              if (isIos() && event?.type === 'mousedown') return;
              emitChange(event, sliderValue);
            }}
          />
        </Card>
      </div>
    </SliderLockContext.Provider>
  );
};
